import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  VStack, Button, ButtonText, Text, HStack, ButtonSpinner,
} from '@gluestack-ui/themed';
import FormField from '@/common/components/forms/FormField';
import {
  FORM_FIELD_CONFIG, VALIDATION_RULES, validateFutureTime,
} from '../constants/slotConstants';
import { getTimezoneDisplayInfo } from '../utils/slotUtils';
import { useEventCalendarMutations } from '../hooks/useEventCalendarMutations';

const computeDurationMinutes = (startTime, endTime) => {
  const duration = dayjs(endTime).diff(dayjs(startTime), 'minute');
  return Math.max(1, duration);
};

const SLOT_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * SlotRescheduleSection
 * ----
 * Progressive disclosure: a Reschedule button reveals datetime controls
 * (duration read-only). Hidden for past or cancelled slots.
 */
const SlotRescheduleSection = ({ slot, activeBookingsCount, onSuccess }) => {
  const { t } = useTranslation();
  const { rescheduleSlot, rescheduleMutation } = useEventCalendarMutations(onSuccess);
  const [isEditing, setIsEditing] = React.useState(false);

  const slotStart = dayjs(slot.startTime);
  const durationMinutes = computeDurationMinutes(slot.startTime, slot.endTime);

  const methods = useForm({
    defaultValues: {
      slotDate: slotStart.format('YYYY-MM-DD'),
      startTime: slotStart.format('HH:mm'),
      durationMinutes,
    },
  });

  const { handleSubmit, watch, reset } = methods;
  const watchedDate = watch('slotDate');

  React.useEffect(() => {
    const start = dayjs(slot.startTime);
    reset({
      slotDate: start.format('YYYY-MM-DD'),
      startTime: start.format('HH:mm'),
      durationMinutes: computeDurationMinutes(slot.startTime, slot.endTime),
    });
    setIsEditing(false);
  }, [slot.slotId, slot.startTime, slot.endTime, reset]);

  const parsedSelectedDate = React.useMemo(() => {
    const parsed = dayjs(watchedDate, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.toDate() : slotStart.toDate();
  }, [watchedDate, slotStart]);

  const { shortName: tzShort } = React.useMemo(
    () => getTimezoneDisplayInfo('en', parsedSelectedDate),
    [parsedSelectedDate],
  );

  const isPastSlot = slotStart.isBefore(dayjs());
  if (isPastSlot || slot.status === 'cancelled') {
    return null;
  }

  const onSubmit = (formData) => {
    rescheduleSlot(slot, formData, activeBookingsCount);
  };

  if (!isEditing) {
    return (
      <Button
        size="sm"
        variant="outline"
        onPress={() => setIsEditing(true)}
        testID="slot-reschedule-open"
      >
        <ButtonText>{t('lecturer.eventCalendar.sidebar.rescheduleOpen')}</ButtonText>
      </Button>
    );
  }

  return (
    <FormProvider {...methods}>
      <VStack space="sm" testID="slot-reschedule-form">
        <Text size="sm" fontWeight="semibold" color="$textHeading">
          {t('lecturer.eventCalendar.sidebar.rescheduleTitle')}
        </Text>

        <FormField
          name="slotDate"
          label={t('lecturer.eventCalendar.sidebar.rescheduleDate')}
          type="date"
          horizontal
          fieldWidth={FORM_FIELD_CONFIG.dateInput.width}
          fieldSize={FORM_FIELD_CONFIG.dateInput.size}
          rules={{
            required: true,
            pattern: {
              value: SLOT_DATE_PATTERN,
              message: t('lecturer.eventCalendar.sidebar.rescheduleDateFormat'),
            },
          }}
        />

        <FormField
          name="startTime"
          label={`${t('lecturer.eventCalendar.sidebar.startTime')} (${tzShort})`}
          type="text"
          horizontal
          fieldWidth={FORM_FIELD_CONFIG.timeInput.width}
          fieldSize={FORM_FIELD_CONFIG.timeInput.size}
          rules={{
            ...VALIDATION_RULES.startTime,
            validate: (value) => validateFutureTime(value, parsedSelectedDate),
          }}
        />

        <FormField
          name="durationMinutes"
          label={t('lecturer.eventCalendar.sidebar.duration')}
          type="text"
          horizontal
          readonly
          fieldWidth={FORM_FIELD_CONFIG.duration.width}
          fieldSize={FORM_FIELD_CONFIG.duration.size}
        />

        <HStack space="sm">
          <Button
            size="sm"
            flex={1}
            variant="outline"
            onPress={() => setIsEditing(false)}
            isDisabled={rescheduleMutation.isPending}
            testID="slot-reschedule-cancel-edit"
          >
            <ButtonText>{t('common.buttons.cancel')}</ButtonText>
          </Button>
          <Button
            size="sm"
            flex={1}
            onPress={handleSubmit(onSubmit)}
            isDisabled={rescheduleMutation.isPending}
            testID="slot-reschedule-submit"
          >
            {rescheduleMutation.isPending ? (
              <HStack space="sm" alignItems="center">
                <ButtonSpinner />
                <ButtonText>{t('lecturer.eventCalendar.sidebar.rescheduling')}</ButtonText>
              </HStack>
            ) : (
              <ButtonText>{t('lecturer.eventCalendar.sidebar.rescheduleButton')}</ButtonText>
            )}
          </Button>
        </HStack>
      </VStack>
    </FormProvider>
  );
};

export default SlotRescheduleSection;
